import { useMemo, useState } from 'react';
import type { Operacion, SolicitudCotizacion } from '@laser/core';
import { api } from '../../api/cliente';
import { useCatalogo } from '../../api/hooks';
import type { ArchivoTrabajo, TrabajoDetalle } from '../../api/tipos';
import { resumenCapa, Vista2D } from '../../componentes/Vista2D';
import { VistaNesting } from '../../componentes/VistaNesting';
import { Insignia, mensajeError, useAvisar } from '../../componentes/ui';
import { descargarBlob } from '../../lib/csv';
import { ETIQUETA_OPERACION, OPERACIONES } from '../../lib/estados';
import { analisisEscalados, escalaDeLinea, escalarAnalisis, hayEscala, textoEscala } from '../../lib/escala';
import { medidas, numero } from '../../lib/formato';
import { cotizacionVigente } from '../../lib/trabajo';

const peso = (b?: number) => (b == null ? '' : b > 1_000_000 ? `${numero(b / 1_000_000)} MB` : `${Math.max(1, Math.round(b / 1000))} KB`);

function ArchivoCaja({ archivo, linea, editable, onOperacion, onLinea }: {
  archivo: ArchivoTrabajo; linea: SolicitudCotizacion['lineas'][number] | undefined; editable: boolean;
  onOperacion: (capaId: string, op: Operacion) => void; onLinea: (c: { cantidad?: number; grabarRasters?: boolean }) => void;
}) {
  const avisar = useAvisar();
  const [resaltar, setResaltar] = useState<string | null>(null);
  const [bajando, setBajando] = useState(false);
  const original = archivo.analisis;
  const escala = escalaDeLinea(linea);
  const escalada = hayEscala(escala);
  // Todo lo que se ve (medidas, capas, piezas, vista 2D) es el diseño ya escalado.
  const a = useMemo(
    () => (original ? escalarAnalisis(original, escala.escalaX, escala.escalaY) : null),
    [original, escala.escalaX, escala.escalaY],
  );
  const operaciones = linea?.operaciones ?? {};

  const descargar = async () => {
    setBajando(true);
    try { descargarBlob(archivo.nombreOriginal, await api.archivos.original(archivo.id)); }
    catch (e) { avisar(mensajeError(e), 'bad'); }
    finally { setBajando(false); }
  };

  return (
    <article className="archivo">
      <div className="archivo__cab">
        <span className="celda-2">
          <b>{archivo.nombreOriginal}</b>
          <small title={escalada && original ? `Tamaño original ${medidas(original.anchoMm, original.altoMm)}` : undefined}>
            {archivo.formato.toUpperCase()}{archivo.bytes ? ` · ${peso(archivo.bytes)}` : ''}
            {a ? ` · ${escalada ? `${textoEscala(escala)} · ` : ''}${medidas(a.anchoMm, a.altoMm)}` : ''}
            {linea ? ` · ${linea.cantidad} ${linea.cantidad === 1 ? 'copia' : 'copias'}` : ''}
          </small>
        </span>
        {escalada && original && (
          <Insignia tono="brand" title={`El cliente pidió el diseño a otra escala. Original ${medidas(original.anchoMm, original.altoMm)}`}>
            {textoEscala(escala)}
          </Insignia>
        )}
        <button type="button" className="btn btn--sm" onClick={descargar} disabled={bajando}>{bajando ? 'Descargando…' : 'Descargar original'}</button>
      </div>
      {!a ? <p className="aviso aviso--warn">Este archivo no tiene análisis (no se pudo convertir).</p> : (
        <div className="archivo__cuerpo">
          <Vista2D analisis={a} operaciones={operaciones} grabarRasters={linea?.grabarRasters ?? true} resaltarCapa={resaltar}
            escalaX={escala.escalaX} escalaY={escala.escalaY} />
          <div>
            <div className="h3">Capas {editable ? '· cambia la operación y recotiza' : ''}</div>
            <div className="capas">
              {a.capas.map((c) => {
                const op = operaciones[c.id] ?? c.operacionSugerida;
                const cambiada = operaciones[c.id] && operaciones[c.id] !== c.operacionSugerida;
                return (
                  <div key={c.id} className="capa" onMouseEnter={() => setResaltar(c.id)} onMouseLeave={() => setResaltar(null)}>
                    <span className="muestra" style={{ background: c.color }} title={c.color} />
                    <span className="celda-2" style={{ minWidth: 0 }}>
                      <b style={{ fontSize: '.82rem' }}>{c.tipo === 'trazo' ? 'Trazo' : 'Relleno'} {c.color}</b>
                      <small>{resumenCapa(c)}{cambiada ? ` · sugerido: ${ETIQUETA_OPERACION[c.operacionSugerida].toLowerCase()}` : ''}</small>
                    </span>
                    <select value={op} disabled={!editable} aria-label={`Operación de la capa ${c.color}`}
                      onChange={(e) => onOperacion(c.id, e.target.value as Operacion)}
                      onFocus={() => setResaltar(c.id)} onBlur={() => setResaltar(null)}>
                      {OPERACIONES.map((o) => <option key={o} value={o}>{ETIQUETA_OPERACION[o]}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
            {a.rasters.length > 0 && (
              <>
                <label className="check" style={{ marginTop: 10 }}>
                  <input type="checkbox" checked={linea?.grabarRasters ?? true} disabled={!editable} onChange={(e) => onLinea({ grabarRasters: e.target.checked })} />
                  Grabar {a.rasters.length === 1 ? 'la imagen' : `${a.rasters.length} imágenes`} ({a.rasters.map((r) => `${Math.round(r.dpi)} dpi`).join(', ')})
                </label>
                {/* Los mismos píxeles sobre más milímetros: al ampliar, la resolución efectiva baja. */}
                {escalada && (linea?.grabarRasters ?? true) && a.rasters.some((r) => r.dpi < 150) && (
                  <div style={{ marginTop: 6 }}>
                    <Insignia tono="warn">
                      Con esta escala la imagen se graba a {Math.round(Math.min(...a.rasters.map((r) => r.dpi)))} dpi
                    </Insignia>
                  </div>
                )}
              </>
            )}
            <div className="linea" style={{ marginTop: 6 }}>
              <span className="muted">Piezas</span><span className="num">{a.piezas.length}</span>
            </div>
            {a.piezas.length > 0 && (
              <div className="linea">
                <span className="muted">Pieza más grande</span>
                <span className="num">{medidas(Math.max(...a.piezas.map((p) => p.anchoMm)), Math.max(...a.piezas.map((p) => p.altoMm)))}</span>
              </div>
            )}
            {a.avisos.length > 0 && (
              <div className="pila" style={{ gap: 4, marginTop: 6 }}>
                {a.avisos.map((av, i) => <Insignia key={i} tono="warn">{av.mensaje}</Insignia>)}
              </div>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

export function Archivos({ trabajo, solicitud, editable, cambios, onOperacion, onLinea }: {
  trabajo: TrabajoDetalle; solicitud: SolicitudCotizacion; editable: boolean; cambios: boolean;
  onOperacion: (archivoId: string, capaId: string, op: Operacion) => void;
  onLinea: (archivoId: string, c: { cantidad?: number; grabarRasters?: boolean }) => void;
}) {
  const cat = useCatalogo();
  const cot = cotizacionVigente(trabajo);
  // El acomodo es el de la cotización guardada: se escala con la solicitud guardada, no con el borrador.
  const analisis = useMemo(() => analisisEscalados(trabajo.archivos, trabajo.solicitud.lineas), [trabajo.archivos, trabajo.solicitud]);
  const nombres = useMemo(() => Object.fromEntries(trabajo.archivos.map((a) => [a.id, a.nombreOriginal])), [trabajo.archivos]);
  const material = cat.material.get(cot.materialId);

  return (
    <>
      <section className="tarjeta" aria-labelledby="arch-t">
        <h2 id="arch-t">Archivos ({trabajo.archivos.length})</h2>
        {trabajo.archivos.length === 0 && <p className="muted">Sin archivos.</p>}
        {trabajo.archivos.map((a) => (
          <ArchivoCaja key={a.id} archivo={a} linea={solicitud.lineas.find((l) => l.archivoId === a.id)} editable={editable}
            onOperacion={(capa, op) => onOperacion(a.id, capa, op)} onLinea={(c) => onLinea(a.id, c)} />
        ))}
      </section>
      <section className="tarjeta" aria-labelledby="nest-t">
        <div className="fila fila--entre">
          <h2 id="nest-t">Acomodo en hoja</h2>
          <span className="meta">{trabajo.cotizacionFinal ? 'cotización final' : 'cotización original'}{material ? ` · ${material.nombre} ${material.espesorMm} mm` : ''}</span>
        </div>
        {cambios && <p className="aviso aviso--warn" style={{ marginBottom: 8 }}>Hay cambios sin recotizar: el acomodo mostrado es el de la cotización guardada.</p>}
        <VistaNesting nesting={cot.nesting} analisis={analisis} colorMaterial={material?.colorVista} nombres={nombres} />
      </section>
    </>
  );
}
